import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { ChangeRequest } from '~/common/entities/ChangeRequest/ChangeRequestType';
import { IdProvider, unwrap } from '../common/apiCommons';
import axios from 'axios';

export enum ChangeRequestKeys {
  CHANGE_REQUESTS = 'CHANGE_REQUESTS',
}

const queries = {
  async getChangeRequestCount(): Promise<number> {
    const res = await axios.get<{ count: number }>('/change_requests/count');
    return res.data.count;
  },

  async getChangeRequestList(page: number, filterState: FilterState) {
    const params: Record<string, unknown> = { page, ...filterState };
    const res = await axios.get<ChangeRequest[]>('/change_requests/', {
      params,
    });
    return unwrap(res);
  },
};

const mutations = {
  async acceptChangeRequest(cr: IdProvider) {
    if (!cr?.id) {
      return null;
    }
    const res = await axios.patch<void>(`/change_request/${cr.id}/handle/`);
    return unwrap(res);
  },
  async updateChangeRequest(changeRequest: ChangeRequest) {
    const res = await axios.patch<ChangeRequest>(
      `/change_request/${changeRequest.id}/`,
      changeRequest,
    );
    return unwrap(res);
  },
  async deleteChangeRequest(cr: IdProvider) {
    if (!cr?.id) {
      return null;
    }
    const res = await axios.delete<void>(`/change_request/${cr.id}/delete/`);
    return unwrap(res);
  },
};

type FilterState = {
  type?: number;
  project?: number;
  employee?: string;
};

const hooks = {
  useChangeRequestCount(
    options?: UseQueryOptions<number, Error>,
  ): UseQueryResult<number> {
    return useQuery<number, Error>({
      queryFn: queries.getChangeRequestCount,
      ...options,
    });
  },
  useChangeRequestsQuery(
    page: number,
    filterState: FilterState,
    options?: UseQueryOptions<ChangeRequest[]>,
  ) {
    const queryClient = useQueryClient();
    const result = useQuery({
      queryKey: [ChangeRequestKeys.CHANGE_REQUESTS, page, filterState],
      queryFn: () => queries.getChangeRequestList(page, filterState),
      ...(options ?? {}),
    });
    const QueryResultAndInvalidate: [typeof result, () => Promise<void>] = [
      result,
      () => queryClient.invalidateQueries([ChangeRequestKeys.CHANGE_REQUESTS]),
    ];
    return QueryResultAndInvalidate;
  },
  useAcceptChangeRequestMutation() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: mutations.acceptChangeRequest,
      async onSuccess() {
        await queryClient.invalidateQueries([
          ChangeRequestKeys.CHANGE_REQUESTS,
        ]);
      },
    });
  },
  useUpdateChangeRequestMutation() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: mutations.updateChangeRequest,
      async onSuccess() {
        await queryClient.invalidateQueries([
          ChangeRequestKeys.CHANGE_REQUESTS,
        ]);
      },
    });
  },
  useDeleteChangeRequestMutation() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: mutations.deleteChangeRequest,
      async onSuccess() {
        await queryClient.invalidateQueries([
          ChangeRequestKeys.CHANGE_REQUESTS,
        ]);
      },
    });
  },
};

const ChangeRequestApi = {
  queries,
  mutations,
  hooks,
};

export default ChangeRequestApi;
